import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled, { css } from 'styled-components'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { getSubscriptionExpiryAlerts } from 'services/billingService'
import { addToCart, getCart } from 'utils/localStorage'
import { PRIVATE_PATHS } from 'utils/paths'

// BP-311: a banner that stays on every page while the user manages a
// Building Passport whose subscription has lapsed or is about to. Closing it
// only hides it for the current browser session — it is meant to be constant.
const DISMISS_KEY = 'bp_subscription_expiry_banner_dismissed'
const REFRESH_MIN_MS = 60 * 1000

const readDismissed = () => {
  try { return window.sessionStorage.getItem(DISMISS_KEY) || '' } catch { return '' }
}
const writeDismissed = signature => {
  try { window.sessionStorage.setItem(DISMISS_KEY, signature) } catch { /* private mode */ }
}

const signatureOf = (expired, expiring) =>
  [...expired.map(bp => `x:${bp.id}`), ...expiring.map(bp => `e:${bp.id}`)].sort().join('|')

const listNames = bps => {
  const names = bps.map(bp => bp.name).filter(Boolean)
  if (names.length <= 2) return names.join(' and ')
  return `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`
}

const daysPhrase = days => {
  if (days === 0) return 'today'
  if (days === 1) return 'in 1 day'
  if (days >= 60) {
    const months = Math.round(days / 30)
    return `in ${months} month${months === 1 ? '' : 's'}`
  }
  return `in ${days} days`
}

const SubscriptionExpiryBanner = ({ enabled }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [alerts, setAlerts] = useState({ expired: [], expiring: [] })
  const [dismissedSignature, setDismissedSignature] = useState(readDismissed)
  const lastFetchRef = useRef(0)

  const refresh = useCallback(() => {
    lastFetchRef.current = Date.now()
    getSubscriptionExpiryAlerts()
      .then(response => {
        const data = response?.data?.data || {}
        setAlerts({
          expired: data.expired || [],
          expiring: data.expiring || []
        })
      })
      .catch(() => {
        // The banner is a courtesy; access enforcement lives server-side, so a
        // failed status check must never break the page.
      })
  }, [])

  useEffect(() => {
    if (!enabled) {
      setAlerts({ expired: [], expiring: [] })
      lastFetchRef.current = 0
      return
    }
    refresh()
  }, [enabled, refresh])

  // Re-check when the user moves around (e.g. straight after a successful
  // reactivation) but never more than once a minute.
  useEffect(() => {
    if (!enabled || !lastFetchRef.current) return
    if (Date.now() - lastFetchRef.current >= REFRESH_MIN_MS) refresh()
  }, [location.pathname, enabled, refresh])

  const { expired, expiring } = alerts
  const signature = useMemo(() => signatureOf(expired, expiring), [expired, expiring])

  if (!enabled || !signature || dismissedSignature === signature) return null

  const dismiss = () => {
    writeDismissed(signature)
    setDismissedSignature(signature)
  }

  const reactivate = () => {
    const cart = getCart()
    expired.forEach(bp => { if (!cart.includes(bp.id)) addToCart(bp.id) })
    navigate(PRIVATE_PATHS.cart)
  }

  const viewExpiring = () => {
    navigate(expiring.length === 1
      ? `${PRIVATE_PATHS.bpList}/${expiring[0].id}`
      : PRIVATE_PATHS.bpList)
  }

  const soonest = expiring[0]

  return (
    <Wrapper role='alert' aria-live='polite'>
      {expired.length > 0 && (
        <Row $tone='expired'>
          <IconTile $tone='expired'><ErrorOutlineIcon style={{ fontSize: 22 }} /></IconTile>
          <Text>
            <Title>
              {expired.length === 1
                ? `The Building Passport for ${expired[0].name} has expired`
                : `${expired.length} Building Passports have expired`}
            </Title>
            <Sub>
              {expired.length === 1
                ? `The subscription ended on ${expired[0].endsAt}. The Building Passport is dormant — its files, tasks and records cannot be accessed until it is reactivated.`
                : `${listNames(expired)} are dormant — their files, tasks and records cannot be accessed until they are reactivated.`}
            </Sub>
          </Text>
          <Action type='button' $tone='expired' onClick={reactivate}>
            <ShoppingCartIcon style={{ fontSize: 17 }} />
            {expired.length === 1 ? 'Reactivate now' : 'Reactivate all'}
          </Action>
        </Row>
      )}
      {expiring.length > 0 && (
        <Row $tone='expiring'>
          <IconTile $tone='expiring'><WarningAmberIcon style={{ fontSize: 22 }} /></IconTile>
          <Text>
            <Title>
              {expiring.length === 1
                ? `The Building Passport for ${soonest.name} expires ${daysPhrase(soonest.days)}`
                : `${expiring.length} Building Passports expire soon`}
            </Title>
            <Sub>
              {expiring.length === 1
                ? `Its subscription ends on ${soonest.endsAt}. Renew it in time to keep uninterrupted access for you and everyone you share it with.`
                : `${listNames(expiring)} — the first one ends ${daysPhrase(soonest.days)} (${soonest.endsAt}).`}
            </Sub>
          </Text>
          <Action type='button' $tone='expiring' onClick={viewExpiring}>
            {expiring.length === 1 ? 'View Building Passport' : 'View Building Passports'}
            <ArrowForwardRoundedIcon style={{ fontSize: 17 }} />
          </Action>
        </Row>
      )}
      <Close type='button' aria-label='Hide for this session' title='Hide for this session' onClick={dismiss}>
        <CloseRoundedIcon style={{ fontSize: 18 }} />
      </Close>
    </Wrapper>
  )
}

export default SubscriptionExpiryBanner

const tones = {
  expired: {
    bg: '#fdecea',
    border: '#f5b5a9',
    accent: '#d14925',
    text: '#7a2513'
  },
  expiring: {
    bg: '#fff7e6',
    border: '#f8d58a',
    accent: '#d48806',
    text: '#7a4b00'
  }
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 20px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  box-sizing: border-box;
  padding: 14px 52px 14px 16px;
  border-radius: 12px;
  border: 1px solid ${({ $tone }) => tones[$tone].border};
  background: ${({ $tone }) => tones[$tone].bg};
  color: ${({ $tone }) => tones[$tone].text};

  @media (max-width: 768px) {
    flex-wrap: wrap;
    padding-right: 44px;
  }
`

const IconTile = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 10px;
  background: #fff;
  color: ${({ $tone }) => tones[$tone].accent};
`

const Text = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    flex: 1 1 calc(100% - 54px);
  }
`

const Title = styled.div`
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 2px;
`

const Sub = styled.div`
  font-size: 13px;
  line-height: 1.5;
  opacity: 0.9;
`

const Action = styled.button`
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 9px 16px;
  border: none;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  color: #fff;
  background: ${({ $tone }) => tones[$tone].accent};
  transition: filter 0.15s, transform 0.15s;

  &:hover {
    filter: brightness(0.92);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  ${({ $tone }) => $tone === 'expiring' && css`
    color: ${tones.expiring.text};
    background: #fff;
    border: 1px solid ${tones.expiring.border};
  `}

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`

const Close = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  cursor: pointer;
  opacity: 0.6;

  &:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.06);
  }
`
